import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { ContactModel } from '@akin-home/contacts';

import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { useDeleteCommand } from '$tablet-app/src/app/chat/hooks/useDeleteCommand';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { useCurrentUserID } from '$tablet-app/src/app/users';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import {
  useDeleteContactModel,
  useGetContactQuery,
  useUpdateContactModel,
} from '@akin-home/contacts/react';
import { Text } from '@akin-home/text/native';
import { showErrorToast, showInfoToast } from '@akin-home/toast/native';
import React, { useCallback, useEffect, useState } from 'react';
import { afterDeleteContact } from '../../functions/afterDeleteContact';
import { afterUpdateContact } from '../../functions/afterUpdateContact';
import { useUpdateContactFormListener } from '../../hooks';
import ContactFormScreenView from '../ContactFormScreen/ContactFormScreenView';

const context = {
  canSave: true,
  canDelete: true,
  fields: {
    name: { type: 'text' },
    email: { type: 'email' },
    phone_number: { type: 'text' },
  },
};

const parameters = {
  contact_id: 'text',
};

export const EditContactScreen = (
  props: RootStackScreenProps<'EditContact'>
) => {
  const {
    route: {
      params: { contact_id },
    },
    navigation: { goBack },
  } = props;

  const user_id = useCurrentUserID();

  useUpdateScreenChatContext(context);

  const [isSubmitting, setIsSubmitting, setNotSubmitting] = useBool();

  const { data: fetchedContact } = useGetContactQuery(contact_id);
  const [contact, setContact] = useState<ContactModel | undefined>(
    fetchedContact
  );

  const [is_member, setIsMember] = useState(!!contact?.user_id);

  const update = useUpdateContactModel();
  const remove = useDeleteContactModel();

  const onSubmit = useSaveCommand(
    useCallback(async () => {
      if (isSubmitting || !contact) return;

      setIsSubmitting();
      showInfoToast({ text1: 'Saving Contact...' });

      try {
        const input: any = { contact };

        if (contact.user_id && !is_member) {
          input.delete_linked_user = true;
        }

        if (!contact.user_id && is_member) {
          input.create_linked_user = {
            sub: useStateStore.getState().current_user?.sub as string,
            username: useStateStore.getState().current_user?.username as string,
          };
        }

        const { data } = await update.mutateAsync(input);

        afterUpdateContact(data);
      } catch (err) {
        setNotSubmitting();
      }

      goBack();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact, isSubmitting, user_id, is_member])
  );

  const onDelete = useDeleteCommand(
    useCallback(async () => {
      showInfoToast({ text1: 'Deleting Contact...' });

      try {
        const res = await remove.mutateAsync({
          id: contact_id,
          user_id: contact?.user_id,
        });

        afterDeleteContact(res.data);
      } catch (err) {
        showErrorToast({ text1: 'Error deleting contact' });
      }

      goBack();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact_id, user_id, contact?.user_id])
  );

  useUpdateContactFormListener(setContact);

  useEffect(() => {
    if (fetchedContact && !contact) {
      setContact(fetchedContact);
      setIsMember(!!fetchedContact.user_id);

      sendBackgroundMessage(intent_labels.open_edit_contact_screen, {
        context: {
          contact: { id: fetchedContact.id, name: fetchedContact.name },
        },
      });
    }
  }, [contact, fetchedContact]);

  if (!contact) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <ContactFormScreenView
        contact={contact}
        submitting={isSubmitting}
        is_member={is_member}
        onSubmit={onSubmit}
        onChange={setContact}
        onCancel={goBack}
        onDelete={onDelete}
        setIsMember={setIsMember}
      />
      <DeveloperScreenInfo
        context={<Text>{JSON.stringify(context, null, 3)}</Text>}
        parameters={<Text>{JSON.stringify(parameters, null, 3)}</Text>}
      />
    </>
  );
};
